<script setup lang="ts">
import { faCircleNotch, faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { GpBtn } from '@gportal/uikit';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { ref, watch } from 'vue';
import useSetupValidation from './composables/useSetupValidation';
import ContinueButton from './shared/ContinueButton.vue';
import WelcomeSection from './shared/WelcomeSection.vue';

const isValid = defineModel<boolean>('isValid', {
  default: false,
});

const emit = defineEmits<{
  nextButtonClicked: [];
}>();

const { addNotification } = useNotifications();

const { userEmailIsConfirmed } = useSetupValidation();

const userStore = useUserStore();
const isSendingConfirmationEmail = ref(false);
const confirmationMailSent = ref(false);
const sendConfirmationEmail = async () => {
  isSendingConfirmationEmail.value = true;
  try {
    await userStore.resendConfirmEMail();

    addNotification({
      type: 'success',
      message: 'Confirmation email sent. Please check your inbox.',
    });
  } catch (error) {
    addNotification({
      type: 'danger',
      message: (error as ServiceError).message,
    });
  } finally {
    isSendingConfirmationEmail.value = false;
  }
};

watch(
  userEmailIsConfirmed,
  () => {
    if (userEmailIsConfirmed.value) {
      isValid.value = true;
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <WelcomeSection
    name="Email confirmation"
    :is-done="userEmailIsConfirmed"
    v-playwright="'welcome-email-section'"
  >
    <template #content>
      <p v-if="userEmailIsConfirmed" class="tw-flex-1" v-playwright="'welcome-email-confirmed'">
        Your email address has been confirmed.
      </p>

      <div v-else class="tw-flex-1" v-playwright="'welcome-email-not-confirmed'">
        <p>
          Your email address is not confirmed. Please confirm your email address before starting.
        </p>
        <GpBtn
          class="tw-mx-auto tw-mt-4 tw-w-auto"
          label="Send confirmation email"
          :disabled="confirmationMailSent || isSendingConfirmationEmail"
          @click="sendConfirmationEmail"
        >
          <FontAwesomeIcon
            v-if="isSendingConfirmationEmail"
            :icon="faCircleNotch"
            class="tw-animate-spin"
          />
          <FontAwesomeIcon v-else :icon="faEnvelope" />
          <span v-if="confirmationMailSent">Confirmation email sent</span>
          <span v-else>Send confirmation email</span>
        </GpBtn>
      </div>
    </template>

    <template #footer>
      <ContinueButton
        class="tw-mx-auto"
        :disabled="!userEmailIsConfirmed"
        v-playwright="'welcome-email-btn'"
        @click="emit('nextButtonClicked')"
      />
    </template>
  </WelcomeSection>
</template>

<style scoped></style>
